/*
 * Copyright (C) 2019-2022 Corvalent Corporation - All Rights Reserved
 * This software is licensed program property and is Proprietary and Confidential.
 * Unauthorized copying of this file via any medium is strictly prohibited unless
 * prior written permission has been obtained by Corvalent Corporation.
 * In addition, the look and feel of this software as well as the user interface
 * design is also Copyright ©.
 * Notice: This software is not open source.
 * This software is subject to the Corvalent Software License Agreement.
 * You should have received a copy of this license with the software.
 * If not, please write to: sales@corvalent.com
 * All information contained herein is, and remains, the property of
 * Corvalent Corporation and its suppliers, if any. This software may also
 * be covered by U.S. and Foreign Patents, patents in progress, and are
 * protected by trade secret or copyright law
 */

import { useQuery } from '@tanstack/react-query';
import { downloadData } from 'aws-amplify/storage';
import {
  getDefaultCustomization,
  getUserCustomization,
} from '../services/customizationService';
import { useFilePath } from './useFilePath';

const setLastUserCustomization = customization => {
  localStorage.setItem('lastUserCustomization', JSON.stringify(customization));
};

const removeLastUserCustomization = () => {
  const lastUserCustomization = getLastUserCustomization();

  if (lastUserCustomization) {
    URL.revokeObjectURL(lastUserCustomization.login_logo);
  }

  localStorage.removeItem('lastUserCustomization');
};

export const getLastUserCustomization = () => {
  return JSON.parse(localStorage.getItem('lastUserCustomization')) || {};
};

const downloadImageToUrl = async imagePath => {
  const { body } = await downloadData({
    path: `files/${imagePath}`,
  }).result;

  return URL.createObjectURL(await body.blob());
};

const getUserCustomizationService = async userId => {
  try {
    const { data } = userId
      ? await getUserCustomization(userId)
      : await getDefaultCustomization();

    return data;
  } catch {
    return null;
  }
};

export const useUserCustomization = ({ email, customizationId } = {}) => {
  const { buildImagePath } = useFilePath();

  const buildImages = async customization => {
    if (!customization) return null;

    const { favicon, login_logo, navbar_logo, id } = customization;
    return {
      ...customization,
      id,
      favicon: await buildImagePath(`customization/favicon/${id}/${favicon}`),
      navbar_logo: await buildImagePath(
        `customization/navbar/${id}/${navbar_logo}`,
      ),
      login_logo: await downloadImageToUrl(
        `customization/login/${id}/${login_logo}`,
      ),
    };
  };

  const query = useQuery({
    queryKey: ['userCustomization', JSON.stringify({ email, customizationId })],
    queryFn: async () => {
      if (!email) return getLastUserCustomization();

      removeLastUserCustomization();

      return getUserCustomizationService(email)
        .then(buildImages)
        .then(customization => {
          if (!customization) return {};

          setLastUserCustomization(customization);
          return customization;
        })
        .catch(error => {
          console.error('Error fetching user customization', error);
          return {};
        });
    },
    placeholderData: {},
    refetchOnWindowFocus: false,
    staleTime: 0,
  });

  return query;
};
